import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Card, Button, Row, Col, Container, Form, Badge, ListGroup } from 'react-bootstrap';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { api } from '../../shared/api';
import { CenteredLoader } from '../../components/LoadingSpinner';
import { getContrastColor } from '../../shared/colorUtils';
import Select from '../../components/form/AppSelect';
import '../../shared/ReactSelectDarkMode.css';
import { usePermissions } from '../../shared/permissions';
import { useDateFormat } from '../../shared/DateFormatContext';
import { useConfirm } from '../../shared/ConfirmContext';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useRealtimeEvents } from '../../shared/useRealtimeEvents';

const selectTheme = theme => ({
  ...theme,
  colors: {
    ...theme.colors,
    primary: 'var(--tblr-primary)',
    neutral0: 'var(--tblr-bg-forms)',
    neutral5: 'var(--tblr-bg-surface)',
    neutral10: 'var(--tblr-bg-surface)',
    neutral20: 'var(--tblr-border-color)',
    neutral30: 'var(--tblr-border-color)',
    neutral80: 'var(--tblr-body-color)',
    neutral90: 'var(--tblr-body-color)'
  }
});

const TaskDetail = () => {
  const navigate = useNavigate();
  const { projectId, taskId } = useParams();
  const { t } = useTranslation();
  const { formatDate, formatDateTime } = useDateFormat();
  const confirm = useConfirm();
  const { canManage } = usePermissions();
  const queryClient = useQueryClient();
  const [error, setError] = useState(null);
  const [editMode, setEditMode] = useState(false);
  const [showCommentForm, setShowCommentForm] = useState(false);
  const [commentText, setCommentText] = useState('');

  const [formData, setFormData] = useState({
    title: '',
    description: '',
    status: '',
    priority: '',
    due_date: '',
    assigned_to: []
  });

  const { data: taskData, isLoading: loading } = useQuery({
    queryKey: ['task-detail', taskId, projectId],
    queryFn: async () => {
      const [taskResponse, projectResponse, commentsResponse] = await Promise.all([
        api.get(`projects/api/project-tasks/${taskId}/`),
        api.get(`projects/api/projects/${projectId}/`),
        api.get(`projects/api/project-task-comments/?task_id=${taskId}`)
      ]);
      return { task: taskResponse, project: projectResponse, comments: commentsResponse };
    },
  });

  useEffect(() => {
    if (taskData?.task) {
      const t = taskData.task;
      setFormData({
        title: t.title || '',
        description: t.description || '',
        status: t.status || '',
        priority: t.priority || '',
        due_date: t.due_date ? t.due_date.split('T')[0] : '',
        assigned_to: t.assigned_to || []
      });
    }
  }, [taskData]);

  const task = taskData?.task ?? null;
  const project = taskData?.project ?? null;
  const comments = taskData?.comments ?? [];

  const editModeRef = useRef(editMode);
  useEffect(() => { editModeRef.current = editMode; }, [editMode]);

  useRealtimeEvents(useCallback((e) => {
    if (e.model !== 'projects.projecttask' && e.model !== 'projects.projecttaskcomment') return;
    if (editModeRef.current) return; // don't clobber an in-progress edit
    queryClient.invalidateQueries({ queryKey: ['task-detail', taskId, projectId] });
  }, [queryClient, taskId, projectId]));

  const { data: refData } = useQuery({
    queryKey: ['task-ref-data'],
    queryFn: async () => {
      const [statusesRes, prioritiesRes, crewRes] = await Promise.all([
        api.get('projects/api/project-task-statuses/'),
        api.get('projects/api/project-task-priorities/'),
        api.get('crew/api/crew/'),
      ]);
      return { statuses: statusesRes, priorities: prioritiesRes, crew: crewRes };
    },
  });
  const taskStatuses = refData?.statuses || [];
  const taskPriorities = refData?.priorities || [];
  const crewMembers = refData?.crew || [];

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
  };

  const handleAssignedToChange = (selectedOption) => {
    setFormData({
      ...formData,
      assigned_to: selectedOption && selectedOption.value !== null ? [selectedOption.value] : []
    });
  };

  const saveTaskMutation = useMutation({
    mutationFn: (data) => api.patch(`projects/api/project-tasks/${taskId}/`, data),
    onSuccess: () => {
      setEditMode(false);
      queryClient.invalidateQueries({ queryKey: ['task-detail', taskId, projectId] });
    },
    onError: (err) => {
      console.error('Error updating task:', err);
      alert(t('projects.tasks.errorUpdating'));
    },
  });

  const addCommentMutation = useMutation({
    mutationFn: () => api.post('projects/api/project-task-comments/', { task: taskId, content: commentText }),
    onSuccess: () => {
      setCommentText('');
      setShowCommentForm(false);
      queryClient.invalidateQueries({ queryKey: ['task-detail', taskId, projectId] });
    },
    onError: (err) => {
      console.error('Error adding comment:', err);
      alert(t('projects.tasks.failedToAddComment'));
    },
  });

  const deleteCommentMutation = useMutation({
    mutationFn: (commentId) => api.delete(`projects/api/project-task-comments/${commentId}/`),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['task-detail', taskId, projectId] }),
    onError: (err) => {
      console.error('Error deleting comment:', err);
      alert(t('projects.tasks.failedToDeleteComment'));
    },
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    saveTaskMutation.mutate(formData);
  };

  const handleCommentSubmit = (e) => {
    e.preventDefault();
    if (!commentText.trim()) return;
    addCommentMutation.mutate();
  };

  const deleteComment = async (commentId) => {
    if (await confirm(t('projects.tasks.confirmDeleteComment'))) {
      deleteCommentMutation.mutate(commentId);
    }
  };

  const commentSubmitting = addCommentMutation.isPending;

  const StatusBadge = ({ detail }) => {
    if (!detail) return <Badge bg="secondary">—</Badge>;
    const bg = detail.color || '#6c757d';
    return (
      <span className="badge" style={{ backgroundColor: bg, color: getContrastColor(bg) }}>
        {detail.name}
      </span>
    );
  };

  const PriorityBadge = ({ detail }) => {
    if (!detail) return <Badge bg="secondary">—</Badge>;
    const bg = detail.color || '#6c757d';
    return (
      <span className="badge" style={{ backgroundColor: bg, color: getContrastColor(bg) }}>
        {detail.name}
      </span>
    );
  };

  const getAssignedToDisplay = () => {
    const details = task?.assigned_to_details;
    if (!details || details.length === 0) return t('projects.tasks.unassigned');
    return details.map(m => m.display_name).join(', ');
  };

  if (loading) {
    return (
      <Container className="mt-4">
        <CenteredLoader />
      </Container>
    );
  }

  if (error) return <Alert variant="danger">{error}</Alert>;
  if (!task) return (
    <Container className="mt-4">
      <Alert variant="warning">{t('projects.tasks.taskNotFound')}</Alert>
    </Container>
  );

  const crewOptions = [
    { value: null, label: t('projects.tasks.unassigned') },
    ...crewMembers.map(m => ({ value: m.id, label: m.display_name || `${m.first_name} ${m.last_name}` }))
  ];
  const selectedCrewOption = crewOptions.find(o =>
    o.value !== null && formData.assigned_to.includes(o.value)
  ) || null;

  // Find the "completed" status id for quick actions
  const completedStatus = taskStatuses.find(s => s.name.toLowerCase().includes('dokon') || s.name.toLowerCase().includes('complet'));
  const isCompleted = completedStatus && task.status === completedStatus.id;
  // Fallback: first non-completed status for reopen
  const firstStatus = taskStatuses[0];

  return (
    <Container className="mt-4">
      <Row className="mb-4">
        <Col>
          <h1>
            {!editMode ? task.title : (
              <Form.Control
                type="text"
                name="title"
                value={formData.title}
                onChange={handleChange}
                required
              />
            )}
          </h1>
          <p className="text-muted">
            {t('projects.tasks.project')}: <a href={`/projects/detail/${project?.id}`}>{project?.name}</a>
          </p>
        </Col>
        <Col className="text-end">
          {!editMode ? (
            <>
              <Button
                variant="outline-secondary"
                className="me-2"
                onClick={() => navigate(`/projects/detail/${projectId}`)}
              >
                {t('projects.backToProject')}
              </Button>
              {canManage && (
                <Button variant="primary" onClick={() => setEditMode(true)}>
                  {t('projects.tasks.editTask')}
                </Button>
              )}
            </>
          ) : (
            <>
              <Button
                variant="outline-secondary"
                className="me-2"
                onClick={() => {
                  setEditMode(false);
                  setFormData({
                    title: task.title || '',
                    description: task.description || '',
                    status: task.status || '',
                    priority: task.priority || '',
                    due_date: task.due_date ? task.due_date.split('T')[0] : '',
                    assigned_to: task.assigned_to || []
                  });
                }}
              >
                {t('common.cancel')}
              </Button>
              <Button variant="primary" onClick={handleSubmit}>
                {t('projects.saveChanges')}
              </Button>
            </>
          )}
        </Col>
      </Row>

      <Row>
        <Col md={8}>
          <Card className="mb-4">
            <Card.Header>
              <h5 className="mb-0">{t('projects.tasks.taskDetails')}</h5>
            </Card.Header>
            <Card.Body>
              {!editMode ? (
                <>
                  <Row className="mb-3">
                    <Col md={3} className="fw-bold">{t('projects.tasks.status')}:</Col>
                    <Col md={9}><StatusBadge detail={task.status_detail} /></Col>
                  </Row>
                  <Row className="mb-3">
                    <Col md={3} className="fw-bold">{t('projects.tasks.priority')}:</Col>
                    <Col md={9}><PriorityBadge detail={task.priority_detail} /></Col>
                  </Row>
                  <Row className="mb-3">
                    <Col md={3} className="fw-bold">{t('projects.tasks.dueDate')}:</Col>
                    <Col md={9}>
                      {task.due_date ? formatDate(task.due_date) : t('common.notSet')}
                    </Col>
                  </Row>
                  <Row className="mb-3">
                    <Col md={3} className="fw-bold">{t('projects.tasks.assignedTo')}:</Col>
                    <Col md={9}>{getAssignedToDisplay()}</Col>
                  </Row>
                  <Row className="mb-3">
                    <Col md={3} className="fw-bold">{t('projects.description')}:</Col>
                    <Col md={9}>
                      {task.description ? (
                        <div style={{ whiteSpace: 'pre-wrap' }}>{task.description}</div>
                      ) : (
                        <em>{t('projects.tasks.noDescription')}</em>
                      )}
                    </Col>
                  </Row>
                </>
              ) : (
                <Form onSubmit={handleSubmit}>
                  <Form.Group className="mb-3">
                    <Form.Label>{t('projects.tasks.status')}</Form.Label>
                    <Form.Select name="status" value={formData.status} onChange={handleChange}>
                      <option value="">—</option>
                      {taskStatuses.map(s => (
                        <option key={s.id} value={s.id}>{s.name}</option>
                      ))}
                    </Form.Select>
                  </Form.Group>

                  <Form.Group className="mb-3">
                    <Form.Label>{t('projects.tasks.priority')}</Form.Label>
                    <Form.Select name="priority" value={formData.priority} onChange={handleChange}>
                      <option value="">—</option>
                      {taskPriorities.map(p => (
                        <option key={p.id} value={p.id}>{p.name}</option>
                      ))}
                    </Form.Select>
                  </Form.Group>

                  <Form.Group className="mb-3">
                    <Form.Label>{t('projects.tasks.dueDate')}</Form.Label>
                    <Form.Control
                      type="date"
                      name="due_date"
                      value={formData.due_date}
                      onChange={handleChange}
                    />
                  </Form.Group>

                  <Form.Group className="mb-3">
                    <Form.Label>{t('projects.tasks.assignedTo')}</Form.Label>
                    <Select
                      value={selectedCrewOption}
                      onChange={handleAssignedToChange}
                      options={crewOptions}
                      isClearable
                      theme={selectTheme}
                      className="react-select-container"
                      classNamePrefix="react-select"
                    />
                  </Form.Group>

                  <Form.Group className="mb-3">
                    <Form.Label>{t('projects.description')}</Form.Label>
                    <Form.Control
                      as="textarea"
                      name="description"
                      value={formData.description || ''}
                      onChange={handleChange}
                      rows={5}
                    />
                  </Form.Group>
                </Form>
              )}
            </Card.Body>
          </Card>

          <Card>
            <Card.Header className="d-flex justify-content-between align-items-center flex-wrap gap-2">
              <h5 className="mb-0">
                {t('projects.tasks.comments')}
                {comments.length > 0 && (
                  <span className="badge ms-2" style={{ backgroundColor: '#6c757d', color: getContrastColor('#6c757d') }}>{comments.length}</span>
                )}
              </h5>
              <Button
                variant="primary"
                size="sm"
                onClick={() => setShowCommentForm(!showCommentForm)}
              >
                {showCommentForm ? t('common.cancel') : t('projects.tasks.addComment')}
              </Button>
            </Card.Header>

            {showCommentForm && (
              <div className="card-body border-bottom" style={{ backgroundColor: 'var(--tblr-bg-surface)' }}>
                <Form onSubmit={handleCommentSubmit}>
                  <Form.Control
                    as="textarea"
                    value={commentText}
                    onChange={(e) => setCommentText(e.target.value)}
                    rows={3}
                    placeholder={t('projects.tasks.yourComment')}
                    required
                    style={{ resize: 'vertical' }}
                  />
                  <div className="d-flex justify-content-end gap-2 mt-2">
                    <Button
                      variant="ghost-secondary"
                      size="sm"
                      onClick={() => { setShowCommentForm(false); setCommentText(''); }}
                    >
                      {t('common.cancel')}
                    </Button>
                    <Button variant="primary" size="sm" type="submit" disabled={commentSubmitting || !commentText.trim()}>
                      {commentSubmitting ? t('projects.tasks.posting') : t('projects.tasks.postComment')}
                    </Button>
                  </div>
                </Form>
              </div>
            )}

            {comments.length === 0 ? (
              <div className="card-body text-center text-muted py-5">
                <svg xmlns="http://www.w3.org/2000/svg" className="icon mb-2 text-muted" width="24" height="24" viewBox="0 0 24 24" strokeWidth="2" stroke="currentColor" fill="none" strokeLinecap="round" strokeLinejoin="round">
                  <path stroke="none" d="M0 0h24v24H0z" fill="none"/>
                  <path d="M3 20l1.3 -3.9a9 8 0 1 1 3.4 2.9l-4.7 1" />
                </svg>
                <div>{t('projects.tasks.noComments')}</div>
              </div>
            ) : (
              <div className="card-body p-0">
                {comments.map((comment, idx) => (
                  <div
                    key={comment.id}
                    className={`px-4 py-3${idx < comments.length - 1 ? ' border-bottom' : ''}`}
                  >
                    <div className="d-flex gap-3">
                      <span
                        className="avatar avatar-sm flex-shrink-0 mt-1"
                        style={comment.author_photo
                          ? { backgroundImage: `url(${comment.author_photo})` }
                          : { backgroundColor: 'var(--tblr-secondary)' }
                        }
                      >
                        {!comment.author_photo && (
                          <span style={{ color: '#fff', fontSize: '12px', fontWeight: 600 }}>
                            {(comment.author_name || '?')[0].toUpperCase()}
                          </span>
                        )}
                      </span>
                      <div className="flex-fill" style={{ minWidth: 0 }}>
                        <div className="d-flex justify-content-between align-items-baseline mb-1">
                          <div>
                            <strong className="me-2">{comment.author_name || t('projects.tasks.unknownUser')}</strong>
                            <small className="text-muted">
                              {formatDateTime(comment.created_at)}
                            </small>
                          </div>
                          <Button
                            variant="ghost-danger"
                            size="sm"
                            className="p-0 ms-2 flex-shrink-0"
                            style={{ lineHeight: 1, fontSize: '12px', color: 'var(--tblr-danger)' }}
                            onClick={() => deleteComment(comment.id)}
                          >
                            {t('common.delete')}
                          </Button>
                        </div>
                        <div style={{ whiteSpace: 'pre-wrap', color: 'var(--tblr-body-color)' }}>
                          {comment.content}
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </Card>
        </Col>

        <Col md={4}>
          <Card className="mb-4">
            <Card.Header>
              <h5 className="mb-0">{t('projects.tasks.activity')}</h5>
            </Card.Header>
            <Card.Body>
              <div className="timeline">
                <div className="timeline-item">
                  <div className="timeline-marker"></div>
                  <div className="timeline-content">
                    <h6 className="mb-0">{t('projects.tasks.taskCreated')}</h6>
                    <small className="text-muted">
                      {formatDateTime(task.created_at)}
                    </small>
                  </div>
                </div>
                {task.updated_at !== task.created_at && (
                  <div className="timeline-item">
                    <div className="timeline-marker"></div>
                    <div className="timeline-content">
                      <h6 className="mb-0">{t('projects.tasks.lastUpdated')}</h6>
                      <small className="text-muted">
                        {formatDateTime(task.updated_at)}
                      </small>
                    </div>
                  </div>
                )}
              </div>
            </Card.Body>
          </Card>

          <Card>
            <Card.Header>
              <h5 className="mb-0">{t('projects.tasks.quickActions')}</h5>
            </Card.Header>
            <ListGroup variant="flush">
              {!isCompleted && completedStatus && (
                <ListGroup.Item action onClick={async () => {
                  try {
                    await api.patch(`projects/api/project-tasks/${taskId}/`, { status: completedStatus.id });
                    fetchTaskData();
                  } catch (err) {
                    console.error('Error marking task as completed:', err);
                    alert(t('projects.tasks.failedToUpdateStatus'));
                  }
                }}>
                  {t('projects.tasks.markCompleted')}
                </ListGroup.Item>
              )}
              {isCompleted && firstStatus && (
                <ListGroup.Item action onClick={async () => {
                  try {
                    await api.patch(`projects/api/project-tasks/${taskId}/`, { status: firstStatus.id });
                    fetchTaskData();
                  } catch (err) {
                    console.error('Error reopening task:', err);
                    alert(t('projects.tasks.failedToUpdateStatus'));
                  }
                }}>
                  {t('projects.tasks.reopenTask')}
                </ListGroup.Item>
              )}
              <ListGroup.Item action onClick={async () => {
                if (await confirm(t('projects.tasks.confirmDelete'))) {
                  api.delete(`projects/api/project-tasks/${taskId}/`)
                    .then(() => navigate(`/projects/detail/${projectId}`))
                    .catch(err => {
                      console.error('Error deleting task:', err);
                      alert(t('projects.tasks.errorDeleting'));
                    });
                }
              }}>
                {t('projects.tasks.deleteTask')}
              </ListGroup.Item>
            </ListGroup>
          </Card>
        </Col>
      </Row>

      <style>{`
        .timeline { position: relative; padding: 10px 0; }
        .timeline:before { content: ''; position: absolute; top: 0; bottom: 0; left: 15px; width: 2px; background: #e9ecef; }
        .timeline-item { display: flex; margin-bottom: 20px; }
        .timeline-marker { width: 12px; height: 12px; border-radius: 50%; background: #007bff; margin-right: 15px; flex-shrink: 0; margin-top: 5px; border: 2px solid white; box-shadow: 0 0 0 2px #007bff; }
        .timeline-content { flex: 1; }
      `}</style>
    </Container>
  );
};

export default TaskDetail;
